// per docs/shape-uc27-alert-engine.md §Implementation plan §11
// per docs/shape-uc-27-alert-triggers-and-state.md §5
// per docs/ui-shape-admin-aggregations-alerts.md §6 (Alert rule), §13 (structured labels)
// per docs/caiet-uc-spec.md §UC27-01 §UC-ALRT-02 §UC-ALRT-03 §UC-ALRT-13
// The rule editor as the wireframe draws it (AdminAlerte.tsx:1899-2716): four
// bands (Identificare, Condiție, Evaluare, Notificare), the name as the one
// identity the administrator types, schedules picked from the wireframe's own
// steps, severity as a segmented control and labels as key/value entries.
// The condition keeps the evaluator's five kinds and offers the builder as a sixth
// (docs/shape-ops-agreed-alerts.md §2.10); the rule keeps its policy.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudChip,
  MudIcon,
  MudInlineMessage,
  MudTextInput,
  MudSegmentedControl,
  MudSelect,
  MudSwitch,
  MudTextarea,
} from '@egov-moldova/mud-react';
import { RouterLink, useToast } from '@simsm/ui-shared';
import {
  type ApiRefusal,
  apiClient,
  apiErrorKey,
  apiRefusal,
  isNormalizedApiError,
  loadFailureKind,
} from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { formatInterval } from './AlertRulesPage';
import { AlertBuilderEditor } from './AlertBuilderEditor';
import {
  builderErrors,
  builderFromParams,
  builderRefusalKey,
  builderToParams,
  emptyBuilder,
  type BuilderCatalog,
  type BuilderForm,
} from './alert-builder';
import { NoReportExceptionsFields } from './NoReportExceptionsFields';
import {
  defaultNoReportExceptions,
  everyWeekdayExcepted,
  noReportExceptionsFromParams,
  noReportExceptionsToParams,
  type NoReportExceptions,
} from './no-report-exceptions';
import type { AlertRuleKind } from '@simsm/shared-types';
import { listWatchedGroups, WATCHED_GROUPS_KEY, type WatchedGroup } from './watched-groups-api';
import { thresholdLabel } from './watched-groups-domain';
import {
  AGREED_ALERT_KINDS,
  AGREED_KIND_DEFAULTS,
  agreedKindErrors,
  agreedKindFromParams,
  agreedKindToParams,
  agreedRange,
  defaultEvalMinutes,
  GROUP_KINDS,
  isAgreedKind,
  type AgreedAlertKind,
  type AgreedKindForm,
} from './alert-agreed-kinds';
import './watched-groups.css';

// The five kinds of the agreed list that `ALERT_RULE_KINDS` gains with lane K are named here as
// well, so the form types the same whether or not that list has them yet.
type RuleKind = AlertRuleKind | AgreedAlertKind;

// 409 codes the save answers for a rule over a watched group (plan.md C5). They are shown under
// the group picker, where the administrator can act on them, not in the page banner.
const GROUP_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'WATCHED_GROUP_NOT_FOUND',
  'WATCHED_GROUP_THRESHOLD_REQUIRED',
]);
type Severity = 'critical' | 'warning' | 'info';

interface RuleDto {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  severity: Severity;
  labels: Record<string, string>;
  expression: { kind: RuleKind; params: Record<string, unknown> };
  evalIntervalSeconds: number;
  forDurationSeconds: number;
  cooldownSeconds: number;
  notifyOnResolve: boolean;
  templateId: string | null;
  policyId: string | null;
  active: boolean;
}

interface NamedDto {
  id: string;
  name: string;
  displayName: string;
}

interface Label {
  key: string;
  value: string;
}

interface FormState {
  name: string;
  displayName: string;
  description: string;
  severity: Severity;
  kind: RuleKind;
  medicineCode: string;
  minQuantity: string;
  daysBefore: string;
  withinHours: string;
  exceptSiteIds: string;
  /** The day and Registrar-type exceptions of a missing-report rule (UC-ALRT-13, CF-56). */
  exceptions: NoReportExceptions;
  windowHours: string;
  /** The parameters of the five agreed kinds (A4, A5, A9, A10, A12), as typed. */
  agreed: AgreedKindForm;
  templateIdParam: string;
  /** The safelisted query's own arguments, carried through untouched. The editor
   *  offers no control for them — `agg_lot_expired_count`'s `windowDays` is the
   *  only one, it is validated and defaulted server-side — but writing `{}` on
   *  every save silently reset a rule the administrator had tuned, so what was
   *  loaded is what is sent back. */
  templateArgs: Record<string, unknown>;
  builder: BuilderForm;
  evalIntervalMinutes: string;
  forDurationMinutes: string;
  /** Not drawn by the wireframe, whose frequency limit belongs to the policy.
   *  Kept as loaded so a save does not reset it. */
  cooldownSeconds: number;
  notifyOnResolve: boolean;
  labels: Label[];
  templateId: string;
  policyId: string;
  active: boolean;
}

// The wireframe's own steps (AdminAlerte.tsx:63-64), in minutes.
const EVAL_STEPS = [15, 30, 60, 360, 1440];
const DURATION_STEPS = [0, 30, 120, 720, 1440];

const LABEL_KEY = /^[A-Za-z0-9_-]+$/;
const LABEL_VALUE_FORBIDDEN = /[=,;]/;

function emptyForm(): FormState {
  return {
    name: '',
    displayName: '',
    description: '',
    severity: 'warning',
    kind: 'stock_below_threshold',
    medicineCode: '',
    minQuantity: '',
    daysBefore: '30',
    withinHours: '48',
    exceptSiteIds: '',
    exceptions: defaultNoReportExceptions(),
    windowHours: '24',
    agreed: { ...AGREED_KIND_DEFAULTS },
    templateIdParam: 'agg_lot_expired_count',
    templateArgs: {},
    builder: emptyBuilder(),
    evalIntervalMinutes: '60',
    forDurationMinutes: '0',
    cooldownSeconds: 24 * 3600,
    notifyOnResolve: false,
    labels: [],
    templateId: '',
    policyId: '',
    active: true,
  };
}

function ruleToForm(r: RuleDto): FormState {
  const params = r.expression.params;
  return {
    name: r.name,
    displayName: r.displayName,
    description: r.description ?? '',
    severity: r.severity,
    kind: r.expression.kind,
    medicineCode: String(params['medicineCode'] ?? ''),
    minQuantity: String(params['minQuantity'] ?? ''),
    daysBefore: String(params['daysBefore'] ?? '30'),
    withinHours: String(params['withinHours'] ?? '48'),
    exceptSiteIds: Array.isArray(params['exceptSiteIds'])
      ? (params['exceptSiteIds'] as string[]).join(', ')
      : '',
    exceptions: noReportExceptionsFromParams(params),
    windowHours: String(params['windowHours'] ?? '24'),
    agreed: agreedKindFromParams(r.expression.kind, params),
    templateIdParam: String(params['templateId'] ?? 'agg_lot_expired_count'),
    templateArgs:
      params['args'] !== null &&
      typeof params['args'] === 'object' &&
      !Array.isArray(params['args'])
        ? (params['args'] as Record<string, unknown>)
        : {},
    builder: r.expression.kind === 'builder' ? builderFromParams(params) : emptyBuilder(),
    evalIntervalMinutes: String(Math.max(1, Math.round(r.evalIntervalSeconds / 60))),
    forDurationMinutes: String(Math.round(r.forDurationSeconds / 60)),
    cooldownSeconds: r.cooldownSeconds,
    notifyOnResolve: r.notifyOnResolve,
    labels: Object.entries(r.labels).map(([key, value]) => ({ key, value })),
    templateId: r.templateId ?? '',
    policyId: r.policyId ?? '',
    active: r.active,
  };
}

function formToParams(
  form: FormState,
  catalog: BuilderCatalog | undefined,
): Record<string, unknown> {
  switch (form.kind) {
    case 'stock_below_threshold':
      return { medicineCode: form.medicineCode, minQuantity: Number(form.minQuantity) };
    case 'lot_expiring':
      return { daysBefore: Number(form.daysBefore) };
    case 'no_report':
      return {
        withinHours: Number(form.withinHours),
        exceptSiteIds: form.exceptSiteIds
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        ...noReportExceptionsToParams(form.exceptions),
      };
    case 'duplicate_report':
      return { windowHours: Number(form.windowHours) };
    case 'sql':
      return { templateId: form.templateIdParam, args: form.templateArgs };
    case 'builder':
      return builderToParams(form.builder, catalog);
    case 'watched_zero_stock':
    case 'watched_depletion_risk':
    case 'sudden_drop':
    case 'watched_export':
    case 'reporting_coverage':
      return agreedKindToParams(form.kind, form.agreed);
    default:
      return {};
  }
}

// A builder refusal names what the compiler refused; anything else goes through the
// application's own error map.
function refusalOf(err: unknown, operationKey: string): ApiRefusal {
  const builderKey = builderRefusalKey(err);
  return builderKey
    ? { titleKey: operationKey, detailKey: builderKey }
    : apiRefusal(err, operationKey);
}

/**
 * The technical name the backend keys a rule on, derived from the name the
 * administrator types (owner ruling, 2026-09-10: the wireframe has no such
 * field). Lower-case ASCII, digits and underscores, with a short suffix so two
 * rules named alike do not collide. Set once, at creation.
 */
export function technicalName(displayName: string, suffix: string): string {
  const stem = displayName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return `${stem || 'rule'}_${suffix}`;
}

export function AlertRuleFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [initial, setInitial] = useState<FormState>(emptyForm);
  const [submitError, setSubmitError] = useState<ApiRefusal | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [labelKey, setLabelKey] = useState('');
  const [labelValue, setLabelValue] = useState('');
  const [labelTried, setLabelTried] = useState(false);
  // The group refusal of the save (C5), kept apart from the banner so it stands under the picker.
  const [groupRefusalKey, setGroupRefusalKey] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['admin-alert-rule', id],
    queryFn: () => apiClient.get<RuleDto>(`/admin/alert-rules/${id}`).then((r) => r.data),
    enabled: isEdit,
  });

  // The safelisted queries the evaluator will run, and the builder's vocabulary.
  // Published by the backend so the administrator picks instead of typing an
  // identifier, and so a new template or field never needs a frontend change
  // (UC-ALRT-02).
  const expressionCatalogQuery = useQuery({
    queryKey: ['alert-expression-catalog'],
    queryFn: () =>
      apiClient
        .get<{ sqlTemplateIds: string[] } & BuilderCatalog>('/admin/alert-rules/expression-catalog')
        .then((r) => r.data),
  });

  const groupsQuery = useQuery({
    queryKey: [WATCHED_GROUPS_KEY],
    queryFn: listWatchedGroups,
    enabled: isAgreedKind(form.kind) && GROUP_KINDS.includes(form.kind),
  });

  const templatesQuery = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      apiClient
        .get<{ templates: NamedDto[] }>('/admin/notification-templates')
        .then((r) => r.data.templates),
  });

  const policiesQuery = useQuery({
    queryKey: ['notification-policies'],
    queryFn: () =>
      apiClient
        .get<{ policies: NamedDto[] }>('/admin/notification-policies')
        .then((r) => r.data.policies),
  });

  useEffect(() => {
    if (detailQuery.data) {
      const next = ruleToForm(detailQuery.data);
      setForm(next);
      setInitial(next);
    }
  }, [detailQuery.data]);

  const create = useMutation({
    mutationFn: (payload: Partial<RuleDto>) =>
      apiClient.post<RuleDto>('/admin/alert-rules', payload).then((r) => r.data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      // The save is confirmed here rather than by the list it navigates to: the
      // toast host is mounted above the router, so the message survives the
      // navigation, and only this side knows which operation ran.
      toast({
        variant: 'success',
        title: t('alerts.toast.rule.created'),
        text: created.displayName,
      });
      navigate('/system-admin/alerts');
    },
    onError: (err: unknown) => {
      if (isGroupRefusal(err)) {
        setGroupRefusalKey(apiErrorKey(err, 'watched_groups.rule.group_refused'));
      } else {
        setSubmitError(refusalOf(err, 'alerts.rules.error.create_failed'));
      }
    },
  });

  const update = useMutation({
    mutationFn: (payload: Partial<RuleDto>) =>
      apiClient.patch<RuleDto>(`/admin/alert-rules/${id}`, payload).then((r) => r.data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
      toast({
        variant: 'success',
        title: t('alerts.toast.rule.saved'),
        text: updated.displayName,
      });
      navigate('/system-admin/alerts');
    },
    onError: (err: unknown) => {
      if (isGroupRefusal(err)) {
        setGroupRefusalKey(apiErrorKey(err, 'watched_groups.rule.group_refused'));
      } else {
        setSubmitError(refusalOf(err, 'alerts.rules.error.save_failed'));
      }
    },
  });

  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

  const requiredText = (raw: string): string | undefined =>
    raw.trim().length === 0 ? t('form.field.required') : undefined;
  const requiredNumber = (raw: string): string | undefined =>
    isNonNegFiniteNumberRequired(raw) ? undefined : t('form.field.invalid_number');

  const displayNameError = requiredText(form.displayName);
  const medicineCodeError =
    form.kind === 'stock_below_threshold' ? requiredText(form.medicineCode) : undefined;
  const minQuantityError =
    form.kind === 'stock_below_threshold' ? requiredNumber(form.minQuantity) : undefined;
  const daysBeforeError =
    form.kind === 'lot_expiring' ? requiredNumber(form.daysBefore) : undefined;
  const withinHoursError = form.kind === 'no_report' ? requiredNumber(form.withinHours) : undefined;
  const exceptionsInvalid = form.kind === 'no_report' && everyWeekdayExcepted(form.exceptions);
  const windowHoursError =
    form.kind === 'duplicate_report' ? requiredNumber(form.windowHours) : undefined;
  const templateIdParamError = form.kind === 'sql' ? requiredText(form.templateIdParam) : undefined;
  const builderInvalid =
    form.kind === 'builder' && Object.keys(builderErrors(form.builder)).length > 0;
  const agreedErrors = isAgreedKind(form.kind) ? agreedKindErrors(form.kind, form.agreed) : {};

  const hasRequiredErrors = Boolean(
    displayNameError ||
    medicineCodeError ||
    minQuantityError ||
    daysBeforeError ||
    withinHoursError ||
    windowHoursError ||
    templateIdParamError ||
    builderInvalid ||
    Object.keys(agreedErrors).length > 0,
  );

  // docs/ui-shape-admin-aggregations-alerts.md §13: a key of letters, digits,
  // underscore or hyphen, a value without delimiters, checked on add.
  const labelKeyError = LABEL_KEY.test(labelKey.trim())
    ? undefined
    : t('alerts.form.label_key_invalid');
  const labelValueError =
    labelValue.trim() && !LABEL_VALUE_FORBIDDEN.test(labelValue)
      ? undefined
      : t('alerts.form.label_value_invalid');

  function addLabel(): void {
    setLabelTried(true);
    if (labelKeyError || labelValueError) return;
    const key = labelKey.trim();
    setForm({
      ...form,
      labels: [
        ...form.labels.filter((label) => label.key !== key),
        { key, value: labelValue.trim() },
      ],
    });
    setLabelKey('');
    setLabelValue('');
    setLabelTried(false);
  }

  const kindOptions: { value: RuleKind; label: string }[] = [
    { value: 'stock_below_threshold', label: t('alerts.kind.stock_below_threshold') },
    { value: 'lot_expiring', label: t('alerts.kind.lot_expiring') },
    { value: 'no_report', label: t('alerts.kind.no_report') },
    { value: 'duplicate_report', label: t('alerts.kind.duplicate_report') },
    { value: 'sql', label: t('alerts.kind.sql') },
    { value: 'builder', label: t('alerts.kind.builder') },
    ...AGREED_ALERT_KINDS.map((kind) => ({ value: kind, label: t(`alerts.kind.${kind}`) })),
  ];

  // A stored schedule off the wireframe's steps still reads as itself.
  function stepOptions(steps: number[], current: string): { value: string; label: string }[] {
    const minutes = [...new Set([...steps, Number(current)])]
      .filter((m) => Number.isFinite(m))
      .sort((a, b) => a - b);
    return minutes.map((m) => ({
      value: String(m),
      label: m === 0 ? t('alerts.interval.minutes', { count: 0 }) : formatInterval(m * 60, t),
    }));
  }

  function handleSave(): void {
    setSubmitError(null);
    setGroupRefusalKey(null);
    setSubmitted(true);
    if (hasRequiredErrors || exceptionsInvalid) return;
    const payload: Partial<RuleDto> = {
      name: isEdit ? form.name : technicalName(form.displayName, Date.now().toString(36).slice(-4)),
      displayName: form.displayName,
      description: form.description || null,
      severity: form.severity,
      labels: Object.fromEntries(form.labels.map((label) => [label.key, label.value])),
      expression: { kind: form.kind, params: formToParams(form, expressionCatalogQuery.data) },
      evalIntervalSeconds: Math.max(60, Number(form.evalIntervalMinutes) * 60),
      forDurationSeconds: Math.max(0, Number(form.forDurationMinutes) * 60),
      cooldownSeconds: form.cooldownSeconds,
      notifyOnResolve: form.notifyOnResolve,
      templateId: form.templateId || null,
      policyId: form.policyId || null,
      active: form.active,
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  }

  const numberInput = (value: string, set: (value: string) => void): JSX.Element => (
    <MudTextInput
      size="md"
      inputMode="numeric"
      value={value}
      onMudInput={(e: CustomEvent<{ value: string }>) => set(e.detail.value)}
    />
  );

  return (
    <FormPage
      breadcrumb={[
        { label: t('alerts.section_title'), href: '/system-admin/alerts' },
        { label: isEdit ? t('alerts.edit.crumb') : t('alerts.new.crumb') },
      ]}
      title={isEdit ? t('alerts.edit.title') : t('alerts.new.title')}
      lede={t('alerts.new.lede')}
      saveLabel={isEdit ? t('alerts.action.save_changes') : t('alerts.action.create_rule')}
      isSaving={create.isPending || update.isPending}
      isDirty={isDirty}
      isLoading={detailQuery.isLoading}
      loadError={isEdit && detailQuery.isError ? loadFailureKind(detailQuery.error) : null}
      refusal={submitError}
      onSave={handleSave}
      onCancel={() => navigate('/system-admin/alerts')}
    >
      <FormSection title={t('alerts.section.basics')} lede={t('alerts.detail.basics_lede')}>
        <FormField
          label={t('alerts.detail.name')}
          required
          hint={t('alerts.field.display_name_hint')}
          error={submitted ? displayNameError : undefined}
        >
          <MudTextInput
            size="md"
            value={form.displayName}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, displayName: e.detail.value })
            }
          />
        </FormField>
        <FormField label={t('alerts.column.type')} required>
          <MudSelect
            size="medium"
            value={form.kind}
            onMudChange={(e: CustomEvent<{ value: string }>) => {
              const kind = e.detail.value as RuleKind;
              setGroupRefusalKey(null);
              // A new rule starts at the periodicity the agreed list gives its kind (plan.md C5);
              // an existing rule keeps the one it has.
              setForm({
                ...form,
                kind,
                ...(isEdit ? {} : { evalIntervalMinutes: String(defaultEvalMinutes(kind)) }),
              });
            }}
          >
            {kindOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MudSelect>
        </FormField>
        <FormField label={t('alerts.field.description')}>
          <MudTextarea
            rows={3}
            resize="vertical"
            value={form.description}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, description: e.detail.value })
            }
          />
        </FormField>
      </FormSection>

      <FormSection title={t('alerts.section.condition')} lede={t('alerts.detail.condition_lede')}>
        {form.kind === 'stock_below_threshold' ? (
          <FormRow columns={2}>
            <FormField
              label={t('alerts.field.medicine_code')}
              required
              error={submitted ? medicineCodeError : undefined}
            >
              <MudTextInput
                size="md"
                value={form.medicineCode}
                onMudInput={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, medicineCode: e.detail.value })
                }
              />
            </FormField>
            <FormField
              label={t('alerts.field.min_quantity')}
              required
              error={submitted ? minQuantityError : undefined}
            >
              {numberInput(form.minQuantity, (minQuantity) => setForm({ ...form, minQuantity }))}
            </FormField>
          </FormRow>
        ) : null}
        {form.kind === 'lot_expiring' ? (
          <FormField
            label={t('alerts.field.days_before')}
            required
            error={submitted ? daysBeforeError : undefined}
          >
            {numberInput(form.daysBefore, (daysBefore) => setForm({ ...form, daysBefore }))}
          </FormField>
        ) : null}
        {form.kind === 'no_report' ? (
          <>
            <FormField
              label={t('alerts.field.within_hours')}
              required
              error={submitted ? withinHoursError : undefined}
            >
              {numberInput(form.withinHours, (withinHours) => setForm({ ...form, withinHours }))}
            </FormField>
            <FormField label={t('alerts.field.except_site_ids')} fullWidth>
              <MudTextInput
                size="md"
                value={form.exceptSiteIds}
                onMudInput={(e: CustomEvent<{ value: string }>) =>
                  setForm({ ...form, exceptSiteIds: e.detail.value })
                }
              />
            </FormField>
            <NoReportExceptionsFields
              value={form.exceptions}
              onChange={(exceptions) => setForm({ ...form, exceptions })}
              submitted={submitted}
            />
          </>
        ) : null}
        {form.kind === 'duplicate_report' ? (
          <FormField
            label={t('alerts.field.window_hours')}
            required
            error={submitted ? windowHoursError : undefined}
          >
            {numberInput(form.windowHours, (windowHours) => setForm({ ...form, windowHours }))}
          </FormField>
        ) : null}
        {form.kind === 'sql' ? (
          <FormField
            label={t('alerts.field.predefined_query')}
            fullWidth
            required
            error={submitted ? templateIdParamError : undefined}
          >
            <MudSelect
              size="medium"
              value={form.templateIdParam}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, templateIdParam: e.detail.value })
              }
            >
              {(expressionCatalogQuery.data?.sqlTemplateIds ?? [form.templateIdParam])
                .filter(Boolean)
                .map((queryId) => (
                  <option key={queryId} value={queryId}>
                    {t(`alerts.query.${queryId}`, queryId)}
                  </option>
                ))}
            </MudSelect>
          </FormField>
        ) : null}
        {isAgreedKind(form.kind) ? (
          <AgreedKindFields
            kind={form.kind}
            value={form.agreed}
            errors={agreedErrors}
            submitted={submitted}
            groups={groupsQuery.data}
            groupsLoading={groupsQuery.isLoading}
            refusalKey={groupRefusalKey}
            onChange={(agreed) => {
              setGroupRefusalKey(null);
              setForm({ ...form, agreed });
            }}
          />
        ) : null}
        {form.kind === 'builder' ? (
          <AlertBuilderEditor
            form={form.builder}
            catalog={expressionCatalogQuery.data}
            submitted={submitted}
            onChange={(builder) => setForm({ ...form, builder })}
          />
        ) : null}
      </FormSection>

      <FormSection title={t('alerts.section.evaluation')} lede={t('alerts.detail.evaluation_lede')}>
        <FormRow columns={3}>
          <FormField label={t('alerts.detail.interval')} required>
            <MudSelect
              size="medium"
              value={form.evalIntervalMinutes}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, evalIntervalMinutes: e.detail.value })
              }
            >
              {stepOptions(EVAL_STEPS, form.evalIntervalMinutes).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </FormField>
          <FormField label={t('alerts.detail.min_duration')} required>
            <MudSelect
              size="medium"
              value={form.forDurationMinutes}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, forDurationMinutes: e.detail.value })
              }
            >
              {stepOptions(DURATION_STEPS, form.forDurationMinutes).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>
        <FormField label={t('alerts.field.severity')} required>
          <MudSegmentedControl
            size="sm"
            value={form.severity}
            aria-label={t('alerts.field.severity')}
            segments={(['critical', 'warning', 'info'] as const).map((severity) => ({
              value: severity,
              label: t(`alerts.severity.${severity}`),
            }))}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, severity: e.detail.value as Severity })
            }
          />
        </FormField>
      </FormSection>

      <FormSection
        title={t('alerts.section.notification')}
        lede={t('alerts.detail.notification_lede')}
      >
        <FormField
          label={t('alerts.detail.transformation')}
          info={t('alerts.form.transformation_info')}
          required
        >
          <MudSelect
            size="medium"
            value={form.notifyOnResolve ? 'fire_and_resolve' : 'fire'}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, notifyOnResolve: e.detail.value === 'fire_and_resolve' })
            }
          >
            <option value="fire">{t('alerts.detail.transformation.on_fire')}</option>
            <option value="fire_and_resolve">
              {t('alerts.detail.transformation.on_fire_and_resolve')}
            </option>
          </MudSelect>
        </FormField>

        {/* docs/ui-shape-admin-aggregations-alerts.md §13: two visible fields, the
            add action on the next line, the added labels as removable chips. */}
        <fieldset className="simsm-form-page__fieldset">
          <legend className="simsm-form-page__field-label">{t('alerts.detail.labels')}</legend>
          <div className="simsm-alerts__label-editor">
            <FormField
              label={t('alerts.form.label_key')}
              error={labelTried ? labelKeyError : undefined}
            >
              <MudTextInput
                size="md"
                value={labelKey}
                placeholder="tip"
                onMudInput={(e: CustomEvent<{ value: string }>) => setLabelKey(e.detail.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  addLabel();
                }}
              />
            </FormField>
            <FormField
              label={t('alerts.form.label_value')}
              error={labelTried ? labelValueError : undefined}
            >
              <MudTextInput
                size="md"
                value={labelValue}
                placeholder="stoc"
                onMudInput={(e: CustomEvent<{ value: string }>) => setLabelValue(e.detail.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  addLabel();
                }}
              />
            </FormField>
            <MudButton
              shape="circular"
              variant="strict"
              appearance="outlined"
              size="md"
              onClick={addLabel}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('alerts.form.label_add')}
            </MudButton>
          </div>
          {form.labels.length > 0 ? (
            <div className="simsm-alerts__label-chips" role="list">
              {form.labels.map((label) => (
                <MudChip
                  key={label.key}
                  role="listitem"
                  type="input"
                  size="sm"
                  removable
                  onMudRemove={() =>
                    setForm({
                      ...form,
                      labels: form.labels.filter((item) => item.key !== label.key),
                    })
                  }
                >
                  <span className="simsm-alerts__meta-key">{label.key}:</span> {label.value}
                </MudChip>
              ))}
            </div>
          ) : null}
        </fieldset>

        <FormField label={t('alerts.field.template')} required>
          <MudSelect
            size="medium"
            value={form.templateId}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, templateId: e.detail.value })
            }
          >
            <option value="">{t('alerts.field.none')}</option>
            {(templatesQuery.data ?? []).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.displayName || tpl.name}
              </option>
            ))}
          </MudSelect>
        </FormField>
        <FormField label={t('alerts.field.policy')}>
          <MudSelect
            size="medium"
            value={form.policyId}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, policyId: e.detail.value })
            }
          >
            <option value="">{t('alerts.field.none')}</option>
            {(policiesQuery.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName || p.name}
              </option>
            ))}
          </MudSelect>
        </FormField>

        <MudSwitch
          checked={form.active}
          onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
            setForm({ ...form, active: e.detail.checked })
          }
        >
          <span slot="label" className="simsm-alerts__switch-label">
            {t('alerts.form.active')}
            <span className="simsm-alerts__switch-hint">{t('alerts.form.active_hint')}</span>
          </span>
        </MudSwitch>
      </FormSection>
    </FormPage>
  );
}

function isNonNegFiniteNumberRequired(raw: string): boolean {
  if (raw.trim().length === 0) return false;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0;
}

function isGroupRefusal(err: unknown): boolean {
  return isNormalizedApiError(err) && GROUP_REFUSAL_CODES.has(err.code);
}

interface AgreedKindFieldsProps {
  kind: AgreedAlertKind;
  value: AgreedKindForm;
  errors: Record<string, string>;
  submitted: boolean;
  groups: WatchedGroup[] | undefined;
  groupsLoading: boolean;
  refusalKey: string | null;
  onChange: (next: AgreedKindForm) => void;
}

// The condition of the five agreed kinds (plan.md C5): a group picker for A4, A5 and A10, and the
// thresholds and windows of A9, A10 and A12. The server checks every one of them again on save.
function AgreedKindFields({
  kind,
  value,
  errors,
  submitted,
  groups,
  groupsLoading,
  refusalKey,
  onChange,
}: AgreedKindFieldsProps): JSX.Element {
  const { t } = useTranslate();
  const needsGroup = GROUP_KINDS.includes(kind);
  const needsThreshold = kind === 'watched_depletion_risk' || kind === 'watched_export';
  const selected = groups?.find((group) => group.id === value.groupId);

  const numberField = (
    name: 'dropPercent' | 'windowHours' | 'windowMinutes' | 'minPercent',
    labelKey: string,
    hintKey: string,
  ): JSX.Element => {
    const refusal = errors[name];
    return (
      <FormField
        label={t(labelKey)}
        required
        hint={t(hintKey)}
        error={submitted && refusal ? t(refusal, agreedRange(name)) : undefined}
      >
        <MudTextInput
          size="md"
          inputMode="numeric"
          value={value[name]}
          onMudInput={(e: CustomEvent<{ value: string }>) =>
            onChange({ ...value, [name]: e.detail.value })
          }
        />
      </FormField>
    );
  };

  const groupError = refusalKey
    ? t(refusalKey)
    : submitted && errors['groupId']
      ? t(errors['groupId'])
      : undefined;

  return (
    <>
      <p className="simsm-form-page__section-lede">{t(`alerts.kind.${kind}.about`)}</p>
      {needsGroup ? (
        <FormField label={t('watched_groups.rule.group')} required fullWidth error={groupError}>
          <MudSelect
            size="medium"
            value={value.groupId}
            disabled={groupsLoading}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              onChange({ ...value, groupId: e.detail.value })
            }
          >
            <option value="">{t('alerts.field.none')}</option>
            {(groups ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </MudSelect>
        </FormField>
      ) : null}
      {needsGroup && selected ? (
        <div className="simsm-watched-group-link">
          <span className="simsm-alerts__muted-text">
            {t('watched_groups.rule.group_summary', {
              threshold: thresholdLabel(selected, t),
              count: selected.memberCount,
            })}
          </span>
          <RouterLink to={`/system-admin/watched-groups/${selected.id}/edit`} size="sm">
            {t('watched_groups.rule.open_group')}
          </RouterLink>
        </div>
      ) : null}
      {needsThreshold && selected && selected.thresholdKind === null ? (
        <MudInlineMessage variant="warning" size="small">
          {t('watched_groups.rule.group_threshold_missing')}
        </MudInlineMessage>
      ) : null}

      {kind === 'sudden_drop' ? (
        <FormRow columns={2}>
          {numberField(
            'dropPercent',
            'watched_groups.rule.drop_percent',
            'watched_groups.rule.drop_percent_hint',
          )}
          {numberField(
            'windowHours',
            'alerts.field.window_hours',
            'watched_groups.rule.window_hours_hint',
          )}
        </FormRow>
      ) : null}
      {kind === 'watched_export'
        ? numberField(
            'windowMinutes',
            'watched_groups.rule.window_minutes',
            'watched_groups.rule.window_minutes_hint',
          )
        : null}
      {kind === 'reporting_coverage' ? (
        <>
          <FormRow columns={2}>
            {numberField(
              'minPercent',
              'watched_groups.rule.min_percent',
              'watched_groups.rule.min_percent_hint',
            )}
            {numberField(
              'windowHours',
              'alerts.field.window_hours',
              'watched_groups.rule.coverage_window_hint',
            )}
          </FormRow>
          <MudInlineMessage variant="info" size="small" hideIcon>
            {t('watched_groups.rule.scope_note')}
          </MudInlineMessage>
        </>
      ) : null}
    </>
  );
}
